"use client";

import { useInfront } from "@/context/infront-context";
import {
  createInstrumentSlug,
  DEFAULT_CONTENT,
} from "@/services/infront/utils";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

const ID = "interest-rate-list";

const INSTRUMENT_MAP: Record<string, { label: string; flag: string }> = {
  "Sweden 2Y Bond":    { label: "Sverige 2 år",   flag: "se" },
  "Sweden 5Y Bond":    { label: "Sverige 5 år",   flag: "se" },
  "Sweden 10Y Bond":   { label: "Sverige 10 år",  flag: "se" },
  "Norway 2Y Bond":    { label: "Norge 2 år",     flag: "no" },
  "Norway 5Y Bond":    { label: "Norge 5 år",     flag: "no" },
  "Norway 10Y Bond":   { label: "Norge 10 år",    flag: "no" },
  "Denmark 2Y Bond":   { label: "Danmark 2 år",   flag: "dk" },
  "Denmark 5Y Bond":   { label: "Danmark 5 år",   flag: "dk" },
  "Denmark 10Y Bond":  { label: "Danmark 10 år",  flag: "dk" },
  "US 2Y Bond":        { label: "USA 2 år",       flag: "us" },
  "US 10Y Bond":       { label: "USA 10 år",      flag: "us" },
  "US 2Y GOV":         { label: "USA 2 år",       flag: "us" },
  "US 10Y GOV":        { label: "USA 10 år",      flag: "us" },
  "STIBOR 3 Months":   { label: "Stibor", flag: "se" },
};

function applyNameMap(container: HTMLElement) {
  container.querySelectorAll('span[aria-label="FULL_NAME"]').forEach((el) => {
    const text = el.textContent?.trim();
    const mapping = text ? INSTRUMENT_MAP[text] : undefined;
    if (!mapping) return;

    el.textContent = mapping.label;

    const flagEl = el.previousElementSibling as HTMLElement | null;
    if (flagEl?.classList.contains("cell-flag")) {
      flagEl.className = `cell-flag cell-flag-${mapping.flag}`;
      flagEl.title = mapping.flag.toUpperCase();
    }
  });
}

type InterestRateListProps = {
  interestRates: { feed: number; ticker: string }[];
};

const InterestRateList = (props: InterestRateListProps) => {
  const router = useRouter();
  const { isReady, load } = useInfront();
  const widgetRef = useRef<any>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    let active = true;

    load?.(["core", "highstock", "hollowcandlestick", "heikinashi"]).then(() => {
      if (!isReady || !active) return;

      widgetRef.current?.destroy();
      widgetRef.current = null;
      observerRef.current?.disconnect();

      const widgetOptions = new window.Infront.QuoteListWidgetOptions();

      widgetOptions.onInstrumentSelected = (instrument: {
        feed: number;
        ticker: string;
      }) => {
        router.push(createInstrumentSlug(instrument.feed, instrument.ticker));
      };

      widgetOptions.instruments = props.interestRates.map(
        (interestRate) =>
          new window.Infront.Instrument(interestRate.feed, interestRate.ticker)
      );

      widgetOptions.widgetTitle = "Räntor";
      widgetOptions.sortable = true;
      widgetOptions.defaultSortedColumns = [];
      widgetOptions.excludeUnsortedState = false;
      widgetOptions.defaultContent = DEFAULT_CONTENT;
      widgetOptions.enableChangeStatusColors = true;

      widgetOptions.columns = [
        { name: "FULL_NAME", flag: true },
        "LAST",
        "CHANGE",
        "TIME",
      ];

      widgetRef.current = window.infront.quoteList(`#${ID}`, widgetOptions);

      const container = document.getElementById(ID);
      if (container) {
        observerRef.current = new MutationObserver(() => applyNameMap(container));
        observerRef.current.observe(container, { childList: true, subtree: true, characterData: true });
      }
    });

    return () => {
      active = false;
      widgetRef.current?.destroy();
      widgetRef.current = null;
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [isReady, router, props.interestRates]);

  return <div className="card" id={ID} />;
};

export default InterestRateList;
